import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import DashboardHeader from "../../../components/DashboardHeader";
import { DefaultAvatarGymOwner } from "../../../assets/images";
import EditGymCard from "../../../components/EditGymCard";
import ClassDropsMetrics from "../../../components/PartnerDashboard/ClassDropsMetrics";
import ClassDropsSettings from "../../../components/PartnerDashboard/ClassDropsSettings";
import PartnerLayout from "../../../components/PartnerDashboard/PartnerLayout";
import OverviewPage from "../../../components/PartnerDashboard/pages/OverviewPage";
import FunnelPage from "../../../components/PartnerDashboard/pages/FunnelPage";
import RevenuePage from "../../../components/PartnerDashboard/pages/RevenuePage";
import MembersPage from "../../../components/PartnerDashboard/pages/MembersPage";
import ExpiringPage from "../../../components/PartnerDashboard/pages/ExpiringPage";
import OpportunitiesPage from "../../../components/PartnerDashboard/pages/OpportunitiesPage";
import LocationsPage from "../../../components/PartnerDashboard/pages/LocationsPage";
import StaffPage from "../../../components/PartnerDashboard/pages/StaffPage";
import ReportsPage from "../../../components/PartnerDashboard/pages/ReportsPage";
import PaymentsPage from "../../../components/PartnerDashboard/pages/PaymentsPage";
import { PageProps } from "../../../components/PartnerDashboard/pages/types";
import { PageStack } from "../../../components/PartnerDashboard/ui";
import { capabilitiesFor } from "../../../components/PartnerDashboard/capabilities";
import { SECTIONS, SectionKey, visibleSections } from "../../../components/PartnerDashboard/sections";
import { useProjection } from "../../../components/PartnerDashboard/useProjection";
import { Member } from "../../../utils/partnerMetrics";
import { Container, LocationSelect } from "./styles";
import { useApi } from "../../../hooks";
import { getBusinessDashboardDataCall } from "../../../api/apiOperations";
import { setPartnerGymInfo } from "../../../slices/appSlice";

const DashboardGymOwners = () => {
  const [activeSection, setActiveSection] = useState<SectionKey>("overview");
  const [showGymCardEdition, setShowGymCardEdition] = useState<boolean>(false);
  const myRef = useRef<HTMLElement | null>(null);
  const dispatch = useDispatch();

  const { partnerGymInfo, partnerGyms, businessDashboardData, userData } = useSelector(
    (state: RootState) => state.appData || {}
  );
  const businessId: string | undefined = partnerGymInfo?.businessId;

  const handleShowGymCard = () => {
    setShowGymCardEdition(!showGymCardEdition);
    !showGymCardEdition && myRef.current?.scrollIntoView();
    showGymCardEdition && window.scrollTo(0, 0);
  };

  const { request: getBusinessDataReq, loading: gymDataLoading } = useApi<any>({
    apiFunc: getBusinessDashboardDataCall,
    initialLoading: true,
  });

  useEffect(() => {
    if (businessId) {
      getBusinessDataReq(businessId);
    }
  }, [businessId]);

  const refresh = useCallback(() => {
    if (businessId) getBusinessDataReq(businessId);
  }, [businessId]);

  const switchGym = useCallback((id: string) => {
    const gym = (partnerGyms as any[]).find((g) => g.businessId === id);
    if (gym) dispatch(setPartnerGymInfo(gym));
  }, [dispatch, partnerGyms]);

  const multiLocation = Array.isArray(partnerGyms) && partnerGyms.length > 1;
  const permissionRole = partnerGymInfo?.permissionRole ?? userData?.permissionRole;
  const capabilities = useMemo(() => capabilitiesFor(permissionRole), [permissionRole]);

  // Switching to a gym where this person has a narrower role can hide the
  // section they were on; fall back to the overview.
  useEffect(() => {
    if (!visibleSections(capabilities, multiLocation).some((s) => s.key === activeSection)) {
      setActiveSection("overview");
    }
  }, [capabilities, multiLocation, activeSection]);

  const { projection, loading: projectionLoading } = useProjection(
    businessId,
    capabilities.canViewProjections
  );

  const members: Member[] = useMemo(() => businessDashboardData || [], [businessDashboardData]);
  const products = partnerGymInfo?.products || [];
  const sectionMeta = SECTIONS.find((s) => s.key === activeSection);

  const pageProps: PageProps = {
    members,
    products,
    capabilities,
    businessId,
    loading: gymDataLoading,
    projection,
    projectionLoading,
    onNavigate: setActiveSection,
    onRefresh: refresh,
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <OverviewPage
            {...pageProps}
            showProfileCardPrompt={!!businessId && !(partnerGymInfo?.products?.length > 1)}
            onCreateProfileCard={handleShowGymCard}
          />
        );
      case "funnel":
        return <FunnelPage {...pageProps} />;
      case "revenue":
        return <RevenuePage {...pageProps} />;
      case "members":
        return <MembersPage {...pageProps} />;
      case "expiring":
        return <ExpiringPage {...pageProps} />;
      case "opportunities":
        return <OpportunitiesPage {...pageProps} />;
      case "locations":
        return (
          <LocationsPage
            gyms={partnerGyms as any[]}
            currentBusinessId={businessId}
            onSwitch={switchGym}
          />
        );
      case "reports":
        return <ReportsPage {...pageProps} studioName={partnerGymInfo?.name} />;
      case "payments":
        return <PaymentsPage />;
      case "classDrops":
        return (
          <PageStack>
            <ClassDropsMetrics businessId={businessId} />
            <ClassDropsSettings businessId={businessId} />
          </PageStack>
        );
      case "staff":
        return <StaffPage {...pageProps} />;
      default:
        return null;
    }
  };

  return (
    <Container>
      <div style={{ display: "none" }}>
        <DashboardHeader avatar={DefaultAvatarGymOwner} />
      </div>

      <PartnerLayout
        active={activeSection}
        onSelect={setActiveSection}
        capabilities={capabilities}
        multiLocation={multiLocation}
        title={sectionMeta?.label || "Overview"}
        subtitle={partnerGymInfo?.name}
        actions={
          multiLocation ? (
            <LocationSelect
              id="gym-location-select"
              aria-label="Location"
              value={businessId || ""}
              onChange={(e) => switchGym(e.target.value)}
            >
              {(partnerGyms as any[]).map((gym) => (
                <option key={gym.businessId} value={gym.businessId}>
                  {gym.name}
                </option>
              ))}
            </LocationSelect>
          ) : undefined
        }
      >
        {renderSection()}
        <section ref={myRef} id="my-section" />
        {showGymCardEdition && <EditGymCard close={handleShowGymCard} />}
      </PartnerLayout>
    </Container>
  );
};

export default DashboardGymOwners;
